import React from 'react';
import { useTranslation } from 'react-i18next';
import TitleText from '@/components/atoms/TitleText';
import InfoText from '@/components/atoms/InfoText';
import StarRating from '@/components/atoms/StarRating';
import ExpandableText from '@/components/atoms/ExpandableText';
import { FullBudgetCriteriaReview } from '@/types/logisticsFeedback';
import { Image } from 'antd';

interface FeedbackFullBudgetReviewProps {
  review: FullBudgetCriteriaReview;
  'data-testid'?: string;
}

export const FeedbackFullBudgetReview: React.FC<
  FeedbackFullBudgetReviewProps
> = ({ review, 'data-testid': dataTestId = 'feedback-full-budget-review' }) => {
  const { t, i18n } = useTranslation();

  const criteria = [
    {
      key: 'quality',
      label: t('logisticsFeedback.productQuality'),
      rating: review.qualityRating,
      reason: review.qualityReason,
    },
    {
      key: 'communication',
      label: t('logisticsFeedback.communication'),
      rating: review.communicationRating,
      reason: review.communicationReason,
    },
    {
      key: 'shipment',
      label: t('logisticsFeedback.shipment'),
      rating: review.shipmentRating,
      reason: review.shipmentReason,
    },
  ].filter((c) => c.rating !== undefined && c.rating !== null);

  return (
    <div
      className="space-y-4 p-4 border rounded-xl bg-light-surface-primary dark:bg-dark-surface-primary border-light-input-border dark:border-dark-input-border"
      data-testid={dataTestId}
    >
      <div
        className="flex items-center justify-between"
        data-testid={`${dataTestId}-header`}
      >
        <TitleText
          text={t('logisticsFeedback.shipmentReview')}
          size="lg"
          className="!font-bold !text-light-primary dark:!text-dark-primary"
          data-testid={`${dataTestId}-title`}
        />
      </div>

      {/* Criteria rating items */}
      <div className="space-y-3" data-testid={`${dataTestId}-criteria-list`}>
        {criteria.map((item, idx) => (
          <div
            key={item.key}
            className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 p-3 rounded-lg bg-soft-background dark:bg-neutral-800/40"
            data-testid={`${dataTestId}-item-${item.key}`}
          >
            <div className="min-w-0">
              <InfoText
                text={item.label}
                className="!font-semibold !text-sm !text-light-primary dark:!text-dark-primary"
                data-testid={`${dataTestId}-label-${item.key}`}
              />
              {item.reason && (
                <InfoText
                  text={item.reason}
                  className="!text-xs !text-light-text-secondary dark:!text-dark-text-secondary mt-0.5"
                  data-testid={`${dataTestId}-reason-${item.key}`}
                />
              )}
            </div>

            <div
              className="flex items-center gap-1.5 shrink-0"
              data-testid={`${dataTestId}-rating-${item.key}`}
            >
              <InfoText
                text={`(${item.rating!.toFixed(1)})`}
                className="!text-sm !font-bold !text-light-primary dark:!text-dark-primary"
                data-testid={`${dataTestId}-value-${item.key}`}
              />
              <StarRating
                value={item.rating!}
                data-testid={`${dataTestId}-stars-${item.key}`}
              />
            </div>
          </div>
        ))}
      </div>

      {/* Review notes */}
      {review.notes && (
        <div
          className="space-y-1 pt-1"
          data-testid={`${dataTestId}-notes-wrapper`}
        >
          <InfoText
            text={t('logisticsFeedback.notes')}
            className="!font-bold !text-sm !text-light-primary dark:!text-dark-primary"
            data-testid={`${dataTestId}-notes-title`}
          />
          <ExpandableText
            text={review.notes}
            limit={180}
            className="!text-sm !text-light-text-secondary dark:!text-dark-text-secondary leading-relaxed"
            data-testid={`${dataTestId}-notes-body`}
          />
        </div>
      )}

      {/* Attachments */}
      {review.attachments && review.attachments.length > 0 && (
        <div
          className="space-y-1.5 pt-1"
          data-testid={`${dataTestId}-attachments-wrapper`}
        >
          <InfoText
            text={t('logisticsFeedback.attachments')}
            className="!font-bold !text-sm !text-light-primary dark:!text-dark-primary"
            data-testid={`${dataTestId}-attachments-title`}
          />
          <div
            className="flex flex-row flex-wrap gap-3"
            data-testid={`${dataTestId}-attachments-list`}
          >
            {review.attachments.map((imgUrl, imgIdx) => (
              <Image
                key={`attachment-${imgIdx}`}
                src={imgUrl}
                width={64}
                height={64}
                alt={`attachment-${imgIdx}`}
                className="rounded-[12px] object-cover border border-light-input-border dark:border-dark-input-border"
                data-testid={`${dataTestId}-attachment-image-${imgIdx}`}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
